import { EventBus } from '../events/EventBus';
import { SurfaceRegistry } from '../surfaceRegistry';
import {
  ModuleManifest,
  ModuleInfo,
  ModuleLoadResult,
  ModuleLoadOptions,
  ModuleUnloadOptions,
  ModuleError,
  ModuleHealthStatus,
  ModuleHealthLevel,
  ModuleStatistics,
  ModulePerformanceMetrics,
  ModuleValidationResult,
  ModuleContext,
  ModuleSystemConfig
} from './types';
import { moduleEventIntegration } from '../integration/ModuleEventIntegration';

export class ModuleLoader {
  private modules: Map<string, ModuleInfo> = new Map();
  private moduleStates: Map<string, 'loading' | 'loaded' | 'unloading' | 'error' | 'inactive'> = new Map();
  private dependencies: Map<string, Set<string>> = new Map();
  private dependents: Map<string, Set<string>> = new Map();
  private statistics: Map<string, ModuleStatistics> = new Map();
  private performanceMetrics: Map<string, ModulePerformanceMetrics> = new Map();
  private eventBus: EventBus;
  private surfaceRegistry: SurfaceRegistry;
  private config: ModuleSystemConfig;

  constructor(
    eventBus: EventBus,
    surfaceRegistry: SurfaceRegistry,
    config: ModuleSystemConfig = {
      modulesDir: './modules',
      cacheDir: './cache',
      tempDir: './temp',
      resolution: {},
      maxConcurrentLoads: 5,
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      allowRemoteModules: false,
      cacheTTL: 3600000,
      cacheMaxSize: 1000000,
      debug: false,
      logLevel: 'error',
      enableProfiling: false
    }
  ) {
    this.eventBus = eventBus;
    this.surfaceRegistry = surfaceRegistry;
    this.config = config;
  }

  /**
   * Set the surface registry after initialization
   */
  public setSurfaceRegistry(surfaceRegistry: SurfaceRegistry): void {
    this.surfaceRegistry = surfaceRegistry;
  }

  /**
   * Load a module with the given options
   */
  async loadModule(moduleName: string, options: ModuleLoadOptions = {}): Promise<ModuleLoadResult> {
    const moduleId = `${moduleName}@${options.version || 'latest'}`;

    try {
      // Check if module is already loaded
      if (this.modules.has(moduleId) && this.moduleStates.get(moduleId) === 'loaded') {
        const existingModule = this.modules.get(moduleId)!;
        return { success: true, moduleId, moduleInfo: existingModule };
      }

      // Set loading state
      this.moduleStates.set(moduleId, 'loading');

      // Load module manifest
      const manifestPath = `${this.config.modulesDir}/${moduleName}/manifest.json`;
      const manifest = await this.loadManifest(manifestPath);

      if (!manifest) {
        throw new Error(`Could not load manifest for module: ${moduleName}`);
      }

      // Check dependencies
      if (manifest.dependencies) {
        for (const depName of Object.keys(manifest.dependencies)) {
          const depVersion = manifest.dependencies[depName];
          const depResult = await this.loadModule(depName, {
            version: depVersion
          });
          if (!depResult.success) {
            throw new Error(`Failed to load dependency ${depName}: ${depResult.error}`);
          }
        }
      }

      // Load module entry points
      const moduleContext: ModuleContext = {
        moduleName,
        version: options.version || 'latest',
        phase: 1,
        eventBus: this.eventBus,
        surfaceRegistry: this.surfaceRegistry,
        commandRegistry: null,
        storage: null,
        config: this.config,
        metadata: {},
        dependencies: new Map(),
        environment: {
          isDevelopment: false,
          isProduction: true,
          nodeEnv: 'production',
          platform: 'browser',
          arch: 'x64'
        }
      };

      const moduleInfo: ModuleInfo = {
        id: moduleId,
        name: moduleName,
        version: manifest.version,
        manifest,
        path: `${this.config.modulesDir}/${moduleName}`,
        loaded: true,
        active: true,
        exports: {},
        entryPoints: manifest.entryPoints || {},
        dependencies: new Set(Object.keys(manifest.dependencies || {})),
        dependents: new Set(),
        loadTime: 0,
        activationTime: 0,
        loadedAt: new Date(),
        accessCount: 0,
        retryCount: 0,
        phase: 1,
        context: moduleContext,
        metadata: {},
        registeredSurfaces: new Set(),
        registeredCommands: new Set(),
        registeredEvents: new Set(),
        health: 'healthy' as ModuleHealthLevel
      };

      // Register surfaces if any
      if (manifest.surfaces && this.surfaceRegistry) {
        this.surfaceRegistry.registerModuleSurfaces(moduleName);
      }

      // Store module info
      this.modules.set(moduleId, moduleInfo);
      this.moduleStates.set(moduleId, 'loaded');

      // Update dependencies and dependents
      if (manifest.dependencies) {
        for (const depName of Object.keys(manifest.dependencies)) {
          if (!this.dependencies.has(moduleId)) {
            this.dependencies.set(moduleId, new Set());
          }
          this.dependencies.get(moduleId)!.add(depName);

          if (!this.dependents.has(depName)) {
            this.dependents.set(depName, new Set());
          }
          this.dependents.get(depName)!.add(moduleName);
        }
      }

      // Emit module loaded event
      this.eventBus.emit('module-loaded', { moduleId, moduleInfo });

      return { success: true, moduleId, moduleInfo };
    } catch (error) {
      this.moduleStates.set(moduleId, 'error');
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Store error in module info
      const moduleInfo: ModuleInfo = {
        id: moduleId,
        name: moduleName,
        version: options.version || 'latest',
        path: `${this.config.modulesDir}/${moduleName}`,
        loaded: false,
        active: false,
        health: 'unhealthy' as ModuleHealthLevel,
        manifest: null as any,
        error: error instanceof Error ? error : new Error(errorMessage),
        loadedAt: new Date(),
        dependencies: new Set<string>(),
        dependents: new Set<string>(),
        context: {} as ModuleContext,
        entryPoints: {},
        exports: {},
        accessCount: 0,
        retryCount: 0,
        phase: 1,
        metadata: {},
        registeredSurfaces: new Set(),
        registeredCommands: new Set(),
        registeredEvents: new Set()
      };

      this.modules.set(moduleId, moduleInfo);

      // Emit module load error event
      const moduleError: ModuleError = {
        moduleId,
        error: error instanceof Error ? error : new Error(errorMessage),
        timestamp: new Date(),
        stack: error instanceof Error ? error.stack : undefined
      } as ModuleError;

      this.eventBus.emit('module-load-error', { moduleId, error: moduleError });

      return { success: false, moduleId, error: errorMessage };
    }
  }

  /**
   * Unload a module with the given options
   */
  async unloadModule(moduleName: string, options: ModuleUnloadOptions = {}): Promise<boolean> {
    const moduleId = `${moduleName}@${options.version || 'latest'}`;

    try {
      const moduleInfo = this.modules.get(moduleId);
      if (!moduleInfo || !moduleInfo.loaded) {
        return false;
      }

      // Set unloading state
      this.moduleStates.set(moduleId, 'unloading');

      // Check dependents
      const dependents = this.dependents.get(moduleId) || new Set();
      if (dependents.size > 0) {
        throw new Error(
          `Cannot unload module ${moduleName}: it has dependents ${Array.from(dependents).join(', ')}`
        );
      }

      // Unregister surfaces
      if (moduleInfo.manifest?.surfaces && this.surfaceRegistry) {
        this.surfaceRegistry.unregisterModuleSurfaces(moduleName);
      }

      // Remove from registry
      this.modules.delete(moduleId);
      this.moduleStates.delete(moduleId);
      this.dependencies.delete(moduleId);
      this.dependents.delete(moduleId);
      this.statistics.delete(moduleId);
      this.performanceMetrics.delete(moduleId);

      // Clear module event handlers
      moduleEventIntegration.clearModuleData(moduleName);

      // Emit module unloaded event
      this.eventBus.emit('module-unloaded', { moduleId });

      // Emit module lifecycle event through module event integration
      moduleEventIntegration.emitModuleLifecycleEvent('unloaded', moduleName, {});

      return true;
    } catch (error) {
      this.moduleStates.set(moduleId, 'error');
      // Emit module lifecycle error event through module event integration
      moduleEventIntegration.emitModuleLifecycleEvent('error', moduleName, {
        loadError: false,
        error: error instanceof Error ? error : new Error(String(error))
      });

      this.eventBus.emit('module-unload-error', {
        moduleId,
        error: error instanceof Error ? error : new Error(String(error))
      });
      return false;
    }
  }

  /**
   * Reload a module
   */
  async reloadModule(moduleName: string, options: ModuleLoadOptions = {}): Promise<ModuleLoadResult> {
    const moduleId = `${moduleName}@${options.version || 'latest'}`;

    // First unload the module
    await this.unloadModule(moduleName, options);

    // Then reload it
    return await this.loadModule(moduleName, options);
  }

  /**
   * Get module information
   */
  getModule(moduleName: string, version?: string): ModuleInfo | null {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    return this.modules.get(moduleId) || null;
  }

  /**
   * Get all modules
   */
  getAllModules(): ModuleInfo[] {
    return Array.from(this.modules.values());
  }

  /**
   * Get loaded modules only
   */
  getLoadedModules(): ModuleInfo[] {
    return Array.from(this.modules.values()).filter((module) => module.loaded);
  }

  /**
   * Check if a module is loaded
   */
  isModuleLoaded(moduleName: string, version?: string): boolean {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    const module = this.modules.get(moduleId);
    return module ? module.loaded : false;
  }

  /**
   * Validate a module
   */
  validateModule(moduleName: string, version?: string): ModuleValidationResult {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    const module = this.modules.get(moduleId);

    if (!module) {
      return {
        valid: false,
        errors: [`Module ${moduleId} not found`],
        warnings: [],
        recommendations: [],
        security: { vulnerabilities: [], warnings: [] },
        compatibility: { dependencies: false, engines: false, api: false }
      };
    }

    if (!module.loaded) {
      return {
        valid: false,
        errors: [`Module ${moduleId} is not loaded`],
        warnings: [],
        recommendations: [],
        security: { vulnerabilities: [], warnings: [] },
        compatibility: { dependencies: false, engines: false, api: false }
      };
    }

    const errors: string[] = [];

    // Check dependencies
    if (module.dependencies) {
      module.dependencies.forEach((dependency: string) => {
        if (!this.isModuleLoaded(dependency)) {
          errors.push(`Dependency ${dependency} is not loaded`);
        }
      });
    }

    // Check manifest validity
    if (!module.manifest) {
      errors.push(`Module ${moduleId} has no manifest`);
    } else {
      if (!module.manifest.name) {
        errors.push(`Module ${moduleId} has no name in manifest`);
      }
      if (!module.manifest.version) {
        errors.push(`Module ${moduleId} has no version in manifest`);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: [],
      recommendations: [],
      security: { vulnerabilities: [], warnings: [] },
      compatibility: { dependencies: true, engines: true, api: true }
    };
  }

  /**
   * Get module dependencies
   */
  getModuleDependencies(moduleName: string, version?: string): string[] {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    const module = this.modules.get(moduleId);
    if (!module || !module.manifest) {
      return [];
    }
    return Object.keys(module.manifest.dependencies || {});
  }

  /**
   * Get module dependents
   */
  getDependents(moduleName: string): string[] {
    const dependents: string[] = [];
    const moduleId = `${moduleName}@${this.modules.get(moduleName)?.version || 'latest'}`;

    this.modules.forEach((module, id) => {
      if (module.manifest?.dependencies && module.manifest.dependencies[moduleName]) {
        dependents.push(module.name);
      }
    });

    return dependents;
  }

  /**
   * Load modules from directory
   */
  async loadModulesFromDirectory(
    directoryPath: string,
    options: ModuleLoadOptions = {}
  ): Promise<{ [moduleId: string]: ModuleLoadResult }> {
    const results: { [moduleId: string]: ModuleLoadResult } = {};

    try {
      // Load the main module from module.json
      const mainModuleResult = await this.loadModule('main', {
        ...options
      });
      results['main@latest'] = mainModuleResult;

      // Load predefined modules based on the manifest
      if (mainModuleResult.success && mainModuleResult.moduleInfo?.manifest) {
        const manifest = mainModuleResult.moduleInfo.manifest;

        // Load workspace surfaces
        if (manifest.surfaces) {
          for (const [surfaceName, surfaceConfig] of Object.entries(manifest.surfaces)) {
            if (surfaceConfig.type === 'workspace') {
              try {
                const surfacePath = `${directoryPath}/surfaces/workspace/${surfaceName}`;
                const surfaceModule = await import(/* @vite-ignore */ surfacePath);

                const context: ModuleContext = {
                  moduleName: `workspace-${surfaceName}`,
                  version: 'latest',
                  phase: 1,
                  eventBus: this.eventBus,
                  surfaceRegistry: this.surfaceRegistry,
                  commandRegistry: null,
                  storage: null,
                  config: this.config,
                  metadata: {},
                  dependencies: new Map(),
                  environment: {
                    isDevelopment: false,
                    isProduction: true,
                    nodeEnv: 'production',
                    platform: 'browser',
                    arch: 'x64'
                  }
                };

                results[`workspace-${surfaceName}@latest`] = {
                  success: true,
                  moduleId: `workspace-${surfaceName}@latest`,
                  moduleInfo: {
                    id: `workspace-${surfaceName}@latest`,
                    name: `workspace-${surfaceName}`,
                    version: 'latest',
                    path: surfacePath,
                    loaded: true,
                    active: true,
                    exports: surfaceModule,
                    entryPoints: {},
                    dependencies: new Set(),
                    dependents: new Set(),
                    accessCount: 0,
                    retryCount: 0,
                    phase: 1,
                    context,
                    metadata: {},
                    registeredSurfaces: new Set(),
                    registeredCommands: new Set(),
                    registeredEvents: new Set(),
                    health: 'healthy' as ModuleHealthLevel,
                    manifest: {
                      name: `workspace-${surfaceName}`,
                      version: 'latest'
                    }
                  }
                };
              } catch (error) {
                results[`workspace-${surfaceName}@latest`] = {
                  success: false,
                  moduleId: `workspace-${surfaceName}@latest`,
                  error: `Failed to load workspace surface ${surfaceName}: ${error}`
                };
              }
            }
          }
        }

        // Load peek surfaces
        if (manifest.surfaces) {
          for (const [surfaceName, surfaceConfig] of Object.entries(manifest.surfaces)) {
            if (surfaceConfig.type === 'peek') {
              try {
                const surfacePath = `${directoryPath}/surfaces/peek/${surfaceName}`;
                const surfaceModule = await import(/* @vite-ignore */ surfacePath);

                const context: ModuleContext = {
                  moduleName: `peek-${surfaceName}`,
                  version: 'latest',
                  phase: 1,
                  eventBus: this.eventBus,
                  surfaceRegistry: this.surfaceRegistry,
                  commandRegistry: null,
                  storage: null,
                  config: this.config,
                  metadata: {},
                  dependencies: new Map(),
                  environment: {
                    isDevelopment: false,
                    isProduction: true,
                    nodeEnv: 'production',
                    platform: 'browser',
                    arch: 'x64'
                  }
                };

                results[`peek-${surfaceName}@latest`] = {
                  success: true,
                  moduleId: `peek-${surfaceName}@latest`,
                  moduleInfo: {
                    id: `peek-${surfaceName}@latest`,
                    name: `peek-${surfaceName}`,
                    version: 'latest',
                    path: surfacePath,
                    loaded: true,
                    active: true,
                    exports: surfaceModule,
                    entryPoints: {},
                    dependencies: new Set(),
                    dependents: new Set(),
                    accessCount: 0,
                    retryCount: 0,
                    phase: 1,
                    context,
                    metadata: {},
                    registeredSurfaces: new Set(),
                    registeredCommands: new Set(),
                    registeredEvents: new Set(),
                    health: 'healthy' as ModuleHealthLevel,
                    manifest: {
                      name: `peek-${surfaceName}`,
                      version: 'latest'
                    }
                  }
                };
              } catch (error) {
                results[`peek-${surfaceName}@latest`] = {
                  success: false,
                  moduleId: `peek-${surfaceName}@latest`,
                  error: `Failed to load peek surface ${surfaceName}: ${error}`
                };
              }
            }
          }
        }

        // Load modal surfaces
        if (manifest.surfaces) {
          for (const [surfaceName, surfaceConfig] of Object.entries(manifest.surfaces)) {
            if (surfaceConfig.type === 'modal') {
              try {
                const surfacePath = `${directoryPath}/surfaces/modal/${surfaceName}`;
                const surfaceModule = await import(/* @vite-ignore */ surfacePath);

                const context: ModuleContext = {
                  moduleName: `modal-${surfaceName}`,
                  version: 'latest',
                  phase: 1,
                  eventBus: this.eventBus,
                  surfaceRegistry: this.surfaceRegistry,
                  commandRegistry: null,
                  storage: null,
                  config: this.config,
                  metadata: {},
                  dependencies: new Map(),
                  environment: {
                    isDevelopment: false,
                    isProduction: true,
                    nodeEnv: 'production',
                    platform: 'browser',
                    arch: 'x64'
                  }
                };

                results[`modal-${surfaceName}@latest`] = {
                  success: true,
                  moduleId: `modal-${surfaceName}@latest`,
                  moduleInfo: {
                    id: `modal-${surfaceName}@latest`,
                    name: `modal-${surfaceName}`,
                    version: 'latest',
                    path: surfacePath,
                    loaded: true,
                    active: true,
                    exports: surfaceModule,
                    entryPoints: {},
                    dependencies: new Set(),
                    dependents: new Set(),
                    accessCount: 0,
                    retryCount: 0,
                    phase: 1,
                    context,
                    metadata: {},
                    registeredSurfaces: new Set(),
                    registeredCommands: new Set(),
                    registeredEvents: new Set(),
                    health: 'healthy' as ModuleHealthLevel,
                    manifest: {
                      name: `modal-${surfaceName}`,
                      version: 'latest'
                    }
                  }
                };
              } catch (error) {
                results[`modal-${surfaceName}@latest`] = {
                  success: false,
                  moduleId: `modal-${surfaceName}@latest`,
                  error: `Failed to load modal surface ${surfaceName}: ${error}`
                };
              }
            }
          }
        }
      }

      return results;
    } catch (error) {
      console.error(`Failed to load modules from directory ${directoryPath}:`, error);
      return results;
    }
  }

  /**
   * Set module path
   */
  setModulePath(path: string): void {
    this.config.modulesDir = path;
  }

  /**
   * Get module path
   */
  getModulePath(): string {
    return this.config.modulesDir;
  }

  /**
   * Get module statistics
   */
  getModuleStatistics(moduleName: string, version?: string): ModuleStatistics | null {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    return this.statistics.get(moduleId) || null;
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(moduleName: string, version?: string): ModulePerformanceMetrics | null {
    const moduleId = `${moduleName}@${version || 'latest'}`;
    return this.performanceMetrics.get(moduleId) || null;
  }

  /**
   * Get module health status
   */
  getModuleHealthStatus(): { [moduleId: string]: ModuleHealthStatus } {
    const status: { [moduleId: string]: ModuleHealthStatus } = {};

    this.modules.forEach((module, moduleId) => {
      status[moduleId] = {
        moduleId,
        moduleName: module.name,
        state: module.loaded ? 'loaded' : 'inactive',
        health: module.health || 'unknown',
        lastCheck: new Date(),
        dependencies: [],
        errors: [],
        warnings: []
      };
    });

    return status;
  }

  /**
   * Load module manifest
   */
  private async loadManifest(manifestPath: string): Promise<ModuleManifest | null> {
    try {
      const response = await fetch(manifestPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.statusText}`);
      }
      const manifest = await response.json();

      // Transform the manifest to match ModuleManifest interface
      return {
        name: manifest.name,
        version: manifest.version,
        description: manifest.description,
        author: manifest.author,
        license: manifest.license,
        dependencies: manifest.dependencies || {},
        optionalDependencies: manifest.optionalDependencies || {},
        peerDependencies: manifest.peerDependencies || {},
        entryPoints: manifest.entryPoints || {},
        main: manifest.main,
        types: manifest.types,
        exports: manifest.exports,
        capabilities: manifest.capabilities,
        config: manifest.config,
        defaults: manifest.defaults,
        hooks: manifest.hooks,
        surfaces: manifest.surfaces,
        commands: manifest.commands,
        events: manifest.events,
        metadata: manifest.metadata,
        engines: manifest.engines
      };
    } catch (error) {
      console.error(`Failed to load manifest from ${manifestPath}:`, error);
      return null;
    }
  }
}
